<template>
  <div class="cert-approve-container">
    <!-- Header -->
    <div class="admin-header">
      <span class="admin-header-title">ระบบอนุมัติใบรับรอง</span>
      <button class="btn-logout" @click="logout">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
        <span>ออกจากระบบ</span>
      </button>
    </div>

    <!-- Tabs -->
    <div class="tab-bar">
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'pending' }"
        @click="activeTab = 'pending'"
      >
        รออนุมัติ
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'history' }"
        @click="activeTab = 'history'"
      >
        ประวัติการอนุมัติ
      </button>
    </div>

    <!-- Search + Filter -->
    <div class="search-row">
      <div class="search-input-wrap">
        <svg
          class="search-icon"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#9999D3"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input v-model="searchText" class="search-input" placeholder="ค้นหา" />
      </div>
      <button
        class="btn-filter"
        @click="showFilter = !showFilter"
        :class="{ active: showFilter }"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="8" y1="12" x2="16" y2="12" />
          <line x1="11" y1="18" x2="13" y2="18" />
        </svg>
        <span>ตัวกรอง</span>
      </button>
    </div>

    <!-- Filter panel -->
    <transition name="filter-drop">
      <div v-if="showFilter" class="filter-panel">
        <div class="filter-field">
          <label>วันที่เริ่มต้น</label>
          <el-date-picker
            v-model="startDate"
            type="date"
            placeholder="เลือกวันที่"
            format="DD/MM/YYYY"
            value-format="YYYY-MM-DD"
            size="large"
            class="w-100"
            :teleported="false"
          />
        </div>
        <div class="filter-field">
          <label>วันที่สิ้นสุด</label>
          <el-date-picker
            v-model="endDate"
            type="date"
            placeholder="เลือกวันที่"
            format="DD/MM/YYYY"
            value-format="YYYY-MM-DD"
            size="large"
            class="w-100"
            :teleported="false"
          />
        </div>
        <div class="filter-actions">
          <el-button
            type="primary"
            style="border-radius:12px;background:#323563;flex:1"
            @click="applyFilter"
            >ยืนยัน</el-button
          >
          <el-button
            type="danger"
            style="border-radius:12px;background:#DA595A;flex:1"
            @click="showFilter = false"
            >ปิด</el-button
          >
        </div>
      </div>
    </transition>
    <!-- Select all + count -->
    <div class="select-all-row">
      <label class="checkbox-label">
        <input
          type="checkbox"
          v-model="selectAll"
          @change="toggleSelectAll"
          class="custom-checkbox"
        />
        <span class="checkbox-box" :class="{ checked: selectAll }">
          <svg
            v-if="selectAll"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              d="M2 6L5 9L10 3"
              stroke="white"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        <span class="select-all-text">เลือกทั้งหมด</span>
      </label>
      <span class="result-count"
        >{{ filteredList.length }} of {{ totalCount }} results</span
      >
    </div>

    <!-- Cards -->
    <div class="card-list">
      <!-- Loading state -->
      <div v-if="isLoading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>กำลังโหลดข้อมูล...</p>
      </div>

      <template v-else>
        <div
          v-for="(item, index) in paginatedList"
          :key="index"
          class="cert-card"
          :class="{ selected: item.selected }"
        >
          <div class="card-header-row">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="item.selected"
                class="custom-checkbox"
                @change="updateSelectAll"
              />
              <span class="checkbox-box" :class="{ checked: item.selected }">
                <svg
                  v-if="item.selected"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                >
                  <path
                    d="M2 6L5 9L10 3"
                    stroke="white"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
            </label>
            <span class="card-name"></span>
          </div>

          <div class="card-body-rows">
            <!-- Always visible: 3 lines -->
            <div class="info-row">
              <span class="info-label">หลักสูตร</span>
              <span class="info-value">
                {{
                  item.syllabusProfile ? item.syllabusProfile.name : "-"
                }}</span
              >
            </div>
            <div class="info-row">
              <span class="info-label">เบอร์ติดต่อ</span>
              <span class="info-value">{{
                item.authorProfile ? item.authorProfile.phoneNumber : "-"
              }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">อีเมล</span>
              <span class="info-value">{{
                item.authorProfile ? item.authorProfile.email : "-"
              }}</span>
            </div>

            <!-- Expanded content -->
            <template v-if="expandedIds.has(item.id)">
              <div class="info-row">
                <span class="info-label">วันเวลาที่สอบ</span>
                <span class="info-value">{{
                  item.examRoomRegister && item.examRoomRegister.examRoomProfile
                    ? formatDate(item.examRoomRegister.examRoomProfile.openTime)
                    : "-"
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">ศูนย์สอบ</span>
                <span class="info-value">{{
                  item.examCenterProfile ? item.examCenterProfile.name : "-"
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">คะแนนที่ได้</span>
                <span class="info-value">{{
                  item.examScore ? item.examScore.score + "/ " + item.examScore.examProfile.totalScore + " คะแนน" : "-"
                }}
              <span v-if="item.examScore && item.examScore.status == 1" class="text-success">(ผ่าน)</span> 
              <span v-if="item.examScore && item.examScore.status == 2" class="text-danger">(ไม่ผ่าน)</span> 
              </span>
              </div>
              <template v-if="activeTab === 'history'">
                <div class="info-row">
                  <span class="info-label">ผู้อนุมัติ</span>
                  <span class="info-value">{{
                    item.approveProfile
                      ? item.approveProfile.prefix +
                        " " +
                        item.approveProfile.firstName +
                        " " +
                        item.approveProfile.lastName
                      : "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">วันเวลาที่อนุมัติ</span>
                  <span class="info-value">{{
                    item.approveProfile ? formatDate(item.certApproveDate) : "-"
                  }}</span>
                </div>
              </template>
            </template>
          </div>

          <!-- Toggle button -->
          <button class="btn-toggle" @click="toggleExpand(item.id)">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#9999D3"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline
                v-if="expandedIds.has(item.id)"
                points="18 15 12 9 6 15"
              />
              <polyline v-else points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>

        <!-- Empty state -->
        <div v-if="filteredList.length === 0" class="empty-state">
          <img src="media/image-app/no-data.svg" alt="" style="height:200px" />
          <p>ไม่พบข้อมูล</p>
        </div>
      </template>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="pagination-bar">
      <!-- First -->
      <button
        class="page-btn"
        :disabled="currentPage === 1"
        @click="currentPage = 1"
        title="หน้าแรก"
      >
        «
      </button>
      <!-- Prev -->
      <button
        class="page-btn"
        :disabled="currentPage === 1"
        @click="currentPage--"
        title="ก่อนหน้า"
      >
        ‹
      </button>

      <template v-for="(p, idx) in visiblePages" :key="idx">
        <span v-if="p === '...'" class="page-ellipsis">…</span>
        <button
          v-else
          class="page-btn"
          :class="{ active: p === currentPage }"
          @click="currentPage = Number(p)"
        >
          {{ p }}
        </button>
      </template>

      <!-- Next -->
      <button
        class="page-btn"
        :disabled="currentPage === totalPages"
        @click="currentPage++"
        title="ถัดไป"
      >
        ›
      </button>
      <!-- Last -->
      <button
        class="page-btn"
        :disabled="currentPage === totalPages"
        @click="currentPage = totalPages"
        title="หน้าสุดท้าย"
      >
        »
      </button>
    </div>

    <!-- Bottom action bar -->
    <div class="bottom-bar">
      <button class="btn-cancel" @click="clearSelection">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
        <span>ยกเลิก</span>
      </button>

      <button
        v-if="activeTab === 'pending'"
        class="btn-approve"
        @click="approveSelected"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span>อนุมัติ</span>
      </button>

      <button
        v-if="activeTab === 'history'"
        class="btn-download"
        @click="downloadSelected"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        <span>ดาวน์โหลดใบรับรอง</span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import Swal from "sweetalert2";
import { PDFDocument } from "pdf-lib";

export default defineComponent({
  name: "CertificateApproveList",
  setup() {
    const activeTab = ref<"pending" | "history">("pending");
    const searchText = ref("");
    const isLoading = ref(false);
    const showFilter = ref(false);
    const filterCourse = ref("");
    const selectAll = ref(false);
    const totalCount = ref(0);
    const startDate = ref("");
    const endDate = ref("");

    const pendingList = ref<any[]>([]);

    const historyList = ref<any[]>([]);

    const currentList = computed(() =>
      activeTab.value === "pending" ? pendingList.value : historyList.value,
    );

    // client-side filter only for course dropdown (keyword/date handled by API)
    const filteredList = computed(() =>
      !filterCourse.value
        ? currentList.value
        : currentList.value.filter(
            (item) => item.course === filterCourse.value,
          ),
    );

    const pageSize = 10;
    const currentPage = ref(1);
    const totalPages = computed(() => Math.ceil(totalCount.value / pageSize));
    // server already paged — just expose filteredList directly
    const paginatedList = computed(() => filteredList.value);

    const visiblePages = computed(() => {
      const total = totalPages.value;
      const cur = currentPage.value;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const pages: (number | "...")[] = [];
      // always show first 2
      pages.push(1, 2);
      const left = Math.max(3, cur - 1);
      const right = Math.min(total - 2, cur + 1);
      if (left > 3) pages.push("...");
      for (let i = left; i <= right; i++) pages.push(i);
      if (right < total - 2) pages.push("...");
      // always show last 2
      pages.push(total - 1, total);
      return pages;
    });

    const toggleSelectAll = () => {
      filteredList.value.forEach((item) => (item.selected = selectAll.value));
    };

    const updateSelectAll = () => {
      selectAll.value =
        filteredList.value.length > 0 &&
        filteredList.value.every((item) => item.selected);
    };

    const clearSelection = () => {
      filteredList.value.forEach((item) => (item.selected = false));
      selectAll.value = false;
    };

    const router = useRouter();

    const logout = () => {
      localStorage.removeItem("u_id");
      localStorage.removeItem("id_token");
      localStorage.removeItem("adminRole");
      localStorage.removeItem("userInfo");
      router.push({ name: "sign-in" });
    };

    const token = () => localStorage.getItem("id_token");

    const getSyllabusRegister = async () => {
      isLoading.value = true;
      try {
        const isPending = activeTab.value === "pending";
        const params: Record<string, number | string | undefined> = {
          certStatus: isPending ? 1 : 2,
          status: isPending ? 4 : 0,
          sizeContents: pageSize,
          page: currentPage.value - 1,
          keyWord: searchText.value || undefined,
          orderBy: 2,
        };
        if (isPending) {
          params.finishStartDate = startDate.value
            ? new Date(startDate.value).getTime()
            : undefined;
          params.finishEndDate = endDate.value
            ? new Date(endDate.value).getTime()
            : undefined;
        } else {
          params.approveStartDate = startDate.value
            ? new Date(startDate.value).getTime()
            : undefined;
          params.approveEndDate = endDate.value
            ? new Date(endDate.value).getTime()
            : undefined;
        }
        const res = await axios.get(
          process.env.VUE_APP_API_URL + "/getSyllabusRegister",
          { params, headers: { token: token() } },
        );
        const content: any[] = res.data?.data?.content ?? [];
        totalCount.value = res.data?.data?.totalElements ?? content.length;

        const enriched: any[] = [];
        for (const item of content) {
          // Step 1: getExamScore
          let examScore: any = null;
          try {
            const scoreRes = await axios.get(
              process.env.VUE_APP_API_URL + "/getExamScore",
              {
                params: { syllabusRegisterId: item.id, examType: 0 },
                headers: { token: token() },
              },
            );
            const scores: any[] = scoreRes.data?.data?.content ?? [];
            examScore = scores.length > 0 ? scores[0] : null;
          } catch { /* ไม่มี score */ }

          // Step 2: getExamRoomRegister
          let examRoomRegister: any = null;
          try {
            const examRoomRegRes = await axios.get(
              process.env.VUE_APP_API_URL + "/getExamRoomRegister",
              {
                params: { userId: item.userId },
                headers: { token: token() },
              },
            );
            const examRoomRegisters: any[] = examRoomRegRes.data?.data?.content ?? [];
            examRoomRegister =
              examRoomRegisters.find(
                (r: any) => r.examRoomProfile?.syllabusId === item.syllabusId,
              ) ?? null;
          } catch { /* ไม่มี examRoomRegister */ }

          // Step 3: getExamCenter (ถ้ามี examCenterId จาก examRoomProfile)
          let examCenterProfile: any = null;
          const examCenterId = examRoomRegister?.examRoomProfile?.examCenterId;
          if (examCenterId) {
            try {
              const examCenterRes = await axios.get(
                process.env.VUE_APP_API_URL + "/getExamCenter",
                {
                  params: { id: examCenterId },
                  headers: { token: token() },
                },
              );
              examCenterProfile = examCenterRes.data?.data?.content?.[0] ?? null;
            } catch { /* ไม่มี examCenter */ }
          }

          enriched.push({
            ...item,
            examRoomRegister,
            examCenterProfile,
            examScore,
          });
        }

        if (isPending) {
          pendingList.value = enriched;
        } else {
          historyList.value = enriched;
        }
        console.log(enriched);
      } catch (e) {
        console.error("getSyllabusRegister error", e);
      } finally {
        isLoading.value = false;
      }
    };

    const applyFilter = () => {
      showFilter.value = false;
      currentPage.value = 1;
      getSyllabusRegister();
    };

    const approveSelected = () => {
      const selected = filteredList.value.filter((i) => i.selected);
      if (selected.length === 0) {
        Swal.fire({
          title: "กรุณาเลือกรายการ",
          icon: "warning",
          confirmButtonText: "ตกลง",
        });
        return;
      }
      Swal.fire({
        title: `อนุมัติ ${selected.length} รายการ?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "อนุมัติ",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#323563",
      }).then(async (result: { isConfirmed: boolean }) => {
        if (result.isConfirmed) {
          // console.log("อนุมัติ:" +selected);

          try {
            await Promise.all(
              selected.map((item) =>
                axios.post(
                  process.env.VUE_APP_API_URL +
                    "/manageSyllabusCertificateApprove" +
                    "?id=" +
                    item.id +
                    "&status=2" +
                    "&cerNo=" +
                    (item.cerNo != null ? item.cerNo : ""),
                  null,
                  { headers: { token: token() } },
                ),
              ),
            );
            Swal.fire({
              title: "อนุมัติสำเร็จ",
              icon: "success",
              confirmButtonText: "ตกลง",
            });
            clearSelection();
            getSyllabusRegister();
          } catch {
            Swal.fire({
              title: "เกิดข้อผิดพลาด",
              icon: "error",
              confirmButtonText: "ตกลง",
            });
          }
        }
      });
    };

    const downloadSelected = async () => {
      const selected = filteredList.value.filter((i) => i.selected);
      if (selected.length === 0) {
        Swal.fire({
          title: "กรุณาเลือกรายการ",
          icon: "warning",
          confirmButtonText: "ตกลง",
        });
        return;
      }

      Swal.fire("กำลังดาวน์โหลด...", "", "info");

      try {
        // ดาวน์โหลด PDF ทุกรายการพร้อมกัน
        const blobs = await Promise.all(
          selected.map(async (item) => {
            const res = await axios.get(
              process.env.VUE_APP_API_URL +
                "/downloadCertificateSyllabus?syllabusRegisterId=" +
                item.id,
              {
                headers: { token: token() },
                responseType: "blob",
              },
            );
            // ถ้า server ส่ง JSON error กลับมาในรูป blob
            if (res.data.type === "application/json") {
              const text = await (res.data as Blob).text();
              throw new Error(JSON.parse(text).message ?? "ดาวน์โหลดไม่สำเร็จ");
            }
            return res.data as Blob;
          }),
        );

        // Merge ทุก PDF เข้าด้วยกัน
        const merged = await PDFDocument.create();
        for (const blob of blobs) {
          const arrayBuffer = await blob.arrayBuffer();
          const pdf = await PDFDocument.load(arrayBuffer);
          const pages = await merged.copyPages(pdf, pdf.getPageIndices());
          pages.forEach((page) => merged.addPage(page));
        }

        const mergedBytes = await merged.save();
        const blob = new Blob(
          [new Uint8Array(mergedBytes).buffer as ArrayBuffer],
          { type: "application/pdf" },
        );
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "certificates.pdf";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        Swal.close();
      } catch (error) {
        const msg =
          error instanceof Error ? error.message : "กรุณาลองใหม่อีกครั้ง";
        Swal.fire({
          title: "ดาวน์โหลดไฟล์ไม่สำเร็จ",
          text: msg,
          icon: "error",
          confirmButtonText: "ตกลง",
        });
      }
    };

    const formatDate = (date: Date | null) => {
      if (!date) return "-";
      return (
        new Date(date).toLocaleDateString("th-TH", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }) +
        " " +
        new Date(date).toLocaleTimeString("th-TH", {
          hour: "2-digit",
          minute: "2-digit",
        }) +
        " น."
      );
    };

    const expandedIds = ref<Set<number>>(new Set());
    const toggleExpand = (id: number) => {
      const next = new Set(expandedIds.value);
      next.has(id) ? next.delete(id) : next.add(id);
      expandedIds.value = next;
    };
    onMounted(async () => {
      try {
        const res = await axios.get(
          process.env.VUE_APP_API_URL +
            "/getProfile?id=" +
            localStorage.getItem("u_id"),
          { headers: { token: localStorage.getItem("id_token") } },
        );
        if (res.data?.data?.adminRole !== 4) {
          router.push({ name: "404" });
          return;
        }
      } catch {
        router.push({ name: "sign-in" });
        return;
      }

      await getSyllabusRegister();
    });

    watch(activeTab, () => {
      currentPage.value = 1;
      getSyllabusRegister();
    });

    watch(searchText, () => {
      currentPage.value = 1;
      getSyllabusRegister();
    });

    watch(currentPage, () => {
      getSyllabusRegister();
    });

    return {
      activeTab,
      searchText,
      showFilter,
      filterCourse,
      selectAll,
      totalCount,
      filteredList,
      toggleSelectAll,
      updateSelectAll,
      clearSelection,
      applyFilter,
      approveSelected,
      downloadSelected,
      formatDate,
      logout,
      expandedIds,
      toggleExpand,
      currentPage,
      totalPages,
      visiblePages,
      paginatedList,
      startDate,
      endDate,
      getSyllabusRegister,
      isLoading,
    };
  },
});
</script>

<style scoped>
.cert-approve-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f5f5f8;
  padding-bottom: 90px;
  font-family: "Sarabun", sans-serif;
}

/* ── Admin Header ── */
.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #323563;
  color: #fff;
}

.admin-header-title {
  font-size: 16px;
  font-weight: 700;
}

.btn-logout {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1.5px solid rgba(255, 255, 255, 0.5);
  background: transparent;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-logout:hover {
  background: rgba(255, 255, 255, 0.15);
}

/* ── Toggle button ── */
.btn-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 6px 0 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

/* ── Tabs ── */
.tab-bar {
  display: flex;
  background: #fff;
  border-bottom: 1px solid #e9e9ef;
}

.tab-btn {
  flex: 1;
  padding: 14px 0;
  font-size: 15px;
  font-weight: 500;
  color: #9999d3;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
}

.tab-btn.active {
  color: #323563;
  font-weight: 700;
  border-bottom-color: #323563;
}

/* ── Search row ── */
.search-row {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: #fff;
}

.search-input-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  background: #f0f0f5;
  border-radius: 10px;
  padding: 0 12px;
}

.search-icon {
  margin-right: 8px;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 14px;
  outline: none;
  color: #323563;
  padding: 10px 0;
}

.search-input::placeholder {
  color: #9999d3;
}

.btn-filter {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1.5px solid #323563;
  background: #fff;
  color: #323563;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  white-space: nowrap;
}

.btn-filter.active,
.btn-filter:hover {
  background: #323563;
  color: #fff;
}

/* ── Filter panel ── */
.filter-panel {
  background: #fff;
  border-bottom: 1px solid #e9e9ef;
  padding: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.filter-field {
  margin-bottom: 12px;
}

.filter-field label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #323563;
  margin-bottom: 6px;
}

.filter-actions {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

.filter-drop-enter-active,
.filter-drop-leave-active {
  transition: all 0.2s ease;
}
.filter-drop-enter-from,
.filter-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ── Select all row ── */
.select-all-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: #fff;
  border-bottom: 1px solid #e9e9ef;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  display: none;
}

.checkbox-box {
  width: 20px;
  height: 20px;
  border-radius: 5px;
  border: 2px solid #9999d3;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
}

.checkbox-box.checked {
  background: #323563;
  border-color: #323563;
}

.select-all-text {
  font-size: 14px;
  color: #323563;
  font-weight: 500;
}

.result-count {
  font-size: 13px;
  color: #9999d3;
}

/* ── Card list ── */
.card-list {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cert-card {
  background: #fff;
  border-radius: 14px;
  border: 1.5px solid #e9e9ef;
  padding: 14px 16px;
  transition: border-color 0.15s;
}

.cert-card.selected {
  border-color: #323563;
}

.card-header-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.card-name {
  font-size: 15px;
  font-weight: 700;
  color: #323563;
}

.card-body-rows {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-row {
  display: flex;
  /* justify-content: space-between; */
  align-items: flex-start;
  font-size: 13px;
}

.info-label {
  color: #9999d3;
  min-width: 90px;
  flex-shrink: 0;
}

.info-value {
  color: #323563;
  font-weight: 500;
  text-align: right;
  word-break: break-all;
}

/* ── Loading state ── */
.loading-state {
  text-align: center;
  padding: 60px 0;
  color: #9999d3;
  font-size: 14px;
}
.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e0e0f0;
  border-top-color: #323563;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 12px;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Empty state ── */
.empty-state {
  text-align: center;
  padding: 40px 0;
  color: #9999d3;
  font-size: 14px;
}

/* ── Pagination ── */
.pagination-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
}

.page-btn {
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1.5px solid #e9e9ef;
  background: #fff;
  color: #323563;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
}

.page-btn.active {
  background: #323563;
  color: #fff;
  border-color: #323563;
}

.page-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.page-ellipsis {
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9999a8;
  font-size: 14px;
  user-select: none;
}

/* ── Bottom bar ── */
.bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  background: #fff;
  border-top: 1px solid #e9e9ef;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
  z-index: 100;
}

.btn-cancel,
.btn-approve,
.btn-download {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 13px 0;
  border-radius: 12px;
  border: none;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
}

.btn-cancel {
  background: #da595a;
  color: #fff;
}

.btn-approve {
  background: #323563;
  color: #fff;
}

.btn-download {
  background: #323563;
  color: #fff;
}

.btn-cancel:active,
.btn-approve:active,
.btn-download:active {
  opacity: 0.8;
}
</style>
